// src/features/team/RolePickers.tsx
// Shared icon/color/permission pickers for team role create + edit dialogs.
// Extracted so RoleFormDialog can serve both TeamRolesPage (create) and
// TeamRoleDetailPage (edit) without duplicating the picker UI.

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@react/hooks';

// ─── Icon definitions ────────────────────────────────────────────

export const ROLE_ICONS = [
  { value: 'shield', label: 'Shield', path: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' },
  { value: 'star', label: 'Star', path: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' },
  { value: 'crown', label: 'Crown', path: 'M2 20h20l-2-12-5 5-3-7-3 7-5-5-2 12z' },
  { value: 'code', label: 'Code', path: 'M16 18l6-6-6-6M8 6l-6 6 6 6' },
  { value: 'wrench', label: 'Wrench', path: 'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z' },
  { value: 'eye', label: 'Eye', path: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z' },
  { value: 'zap', label: 'Zap', path: 'M13 2L3 14h9l-1 10 10-12h-9l1-10z' },
  { value: 'heart', label: 'Heart', path: 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z' },
  { value: 'users', label: 'Users', path: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 7a4 4 0 100-8 4 4 0 000 8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75' },
  { value: 'lock', label: 'Lock', path: 'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2z M7 11V7a5 5 0 0110 0v4' },
  { value: 'bell', label: 'Bell', path: 'M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 01-3.46 0' },
  { value: 'flag', label: 'Flag', path: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z M4 22v-7' },
  { value: 'bookmark', label: 'Bookmark', path: 'M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z' },
  { value: 'target', label: 'Target', path: 'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z M12 18a6 6 0 100-12 6 6 0 000 12z M12 14a2 2 0 100-4 2 2 0 000 4z' },
  { value: 'award', label: 'Award', path: 'M12 15a7 7 0 100-14 7 7 0 000 14z M8.21 13.89L7 23l5-3 5 3-1.21-9.12' },
  { value: 'compass', label: 'Compass', path: 'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z' },
];

export const PRESET_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
  '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e',
  '#78716c',
];

// ─── Permission definitions ─────────────────────────────────────

export interface PermDef {
  id: string;
  action: string;
  resource: string;
  scope: string;
}

export interface PermGroup {
  key: string;
  permissions: PermDef[];
}

export const PERMISSION_GROUPS: PermGroup[] = [
  {
    key: 'teamManagement',
    permissions: [
      { id: 'perm_admin_team', action: 'admin', resource: 'team', scope: 'all' },
      { id: 'perm_manage_team', action: 'manage', resource: 'team', scope: 'all' },
      { id: 'perm_view_team', action: 'view', resource: 'team', scope: 'all' },
      { id: 'perm_update_team', action: 'update', resource: 'team', scope: 'all' },
      { id: 'perm_delete_team', action: 'delete', resource: 'team', scope: 'all' },
    ],
  },
  {
    key: 'members',
    permissions: [
      { id: 'perm_add_member', action: 'add', resource: 'member', scope: 'all' },
      { id: 'perm_remove_member', action: 'remove', resource: 'member', scope: 'all' },
      { id: 'perm_view_members', action: 'view', resource: 'member', scope: 'all' },
    ],
  },
  {
    key: 'invitations',
    permissions: [
      { id: 'perm_invite_user', action: 'invite', resource: 'user', scope: 'all' },
      { id: 'perm_cancel_invitation', action: 'cancel', resource: 'invitation', scope: 'all' },
    ],
  },
  {
    key: 'applications',
    permissions: [
      { id: 'perm_approve_application', action: 'approve', resource: 'application', scope: 'all' },
    ],
  },
  {
    key: 'rolesAndPermissions',
    permissions: [
      { id: 'perm_manage_roles', action: 'manage', resource: 'role', scope: 'all' },
      { id: 'perm_assign_roles', action: 'assign', resource: 'role', scope: 'all' },
      { id: 'perm_manage_permissions', action: 'manage', resource: 'permission', scope: 'all' },
    ],
  },
  {
    key: 'ownership',
    permissions: [
      { id: 'perm_manage_ownership', action: 'manage', resource: 'ownership', scope: 'all' },
    ],
  },
  {
    key: 'media',
    permissions: [
      { id: 'perm_manage_avatars', action: 'manage', resource: 'avatar', scope: 'all' },
      { id: 'perm_manage_banners', action: 'manage', resource: 'banner', scope: 'all' },
    ],
  },
  {
    key: 'settings',
    permissions: [
      { id: 'perm_manage_settings', action: 'manage', resource: 'settings', scope: 'all' },
      { id: 'perm_view_settings', action: 'view', resource: 'settings', scope: 'all' },
      { id: 'perm_manage_user_settings', action: 'manage', resource: 'user_settings', scope: 'all' },
    ],
  },
  {
    key: 'schedules',
    permissions: [
      { id: 'perm_manage_schedules', action: 'manage', resource: 'schedule', scope: 'all' },
      { id: 'perm_view_schedules', action: 'view', resource: 'schedule', scope: 'all' },
    ],
  },
  {
    key: 'advanced',
    permissions: [
      { id: 'perm_manage_delegations', action: 'manage', resource: 'delegation', scope: 'all' },
      { id: 'perm_manage_temporary_roles', action: 'manage', resource: 'temporary_role', scope: 'all' },
      { id: 'perm_view_logs', action: 'view', resource: 'log', scope: 'all' },
      { id: 'perm_view_automation', action: 'view', resource: 'automation', scope: 'all' },
    ],
  },
];

export const ALL_PERM_IDS = PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => p.id));

// ─── Inline SVG helper ───────────────────────────────────────────

export function RoleIcon({ icon, color, size = 'md' }: { icon?: string; color?: string; size?: 'sm' | 'md' | 'lg' }) {
  const iconDef = ROLE_ICONS.find((i) => i.value === icon);
  const sizeClass = size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-6 w-6' : 'h-5 w-5';
  const wrapSize = size === 'sm' ? 'h-7 w-7' : size === 'lg' ? 'h-10 w-10' : 'h-8 w-8';

  return (
    <div
      className={`${wrapSize} rounded-lg flex items-center justify-center flex-shrink-0`}
      style={{ backgroundColor: color ? `${color}20` : 'var(--muted)' }}
    >
      {iconDef ? (
        <svg
          className={sizeClass}
          fill="none"
          stroke={color || 'currentColor'}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          viewBox="0 0 24 24"
        >
          <path d={iconDef.path} />
        </svg>
      ) : (
        <div className={`${size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} rounded-full`} style={{ backgroundColor: color || '#94a3b8' }} />
      )}
    </div>
  );
}

// ─── Icon Picker ─────────────────────────────────────────────────

export function IconPicker({ value, onChange, color }: {
  value: string;
  onChange: (v: string) => void;
  color: string;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = ROLE_ICONS.find((i) => i.value === value);

  return (
    <div className="flex flex-col gap-1.5" ref={ref}>
      <label className="text-xs font-medium text-muted-foreground">{t('team.roleField.icon')}</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2.5 w-full h-9 px-3 rounded-md border border-input bg-background text-sm text-foreground hover:border-ring transition-colors"
        >
          {selected ? (
            <>
              <RoleIcon icon={selected.value} color={color} size="sm" />
              <span className="flex-1 text-left">{selected.label}</span>
            </>
          ) : (
            <span className="flex-1 text-left text-muted-foreground">{t('team.roleField.iconPlaceholder')}</span>
          )}
          <svg className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
        {open && (
          <div className="absolute z-30 top-full left-0 mt-1.5 w-full min-w-[16rem] bg-popover border border-border rounded-xl shadow-xl p-3">
            <div className="grid grid-cols-4 gap-1.5">
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); }}
                className={`flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-muted transition-colors ${!value ? 'bg-accent ring-1 ring-primary' : ''}`}
              >
                <div className="h-6 w-6 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center">
                  <svg className="h-3 w-3 text-muted-foreground/40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M18 6L6 18M6 6l12 12" /></svg>
                </div>
                <span className="text-[10px] text-muted-foreground">{t('common.none')}</span>
              </button>
              {ROLE_ICONS.map((icon) => (
                <button
                  key={icon.value}
                  type="button"
                  onClick={() => { onChange(icon.value); setOpen(false); }}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-muted transition-colors ${value === icon.value ? 'bg-accent ring-1 ring-primary' : ''}`}
                  title={icon.label}
                >
                  <svg className="h-5 w-5" fill="none" stroke={color || 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d={icon.path} />
                  </svg>
                  <span className="text-[10px] text-muted-foreground truncate w-full text-center">{icon.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Color Picker ────────────────────────────────────────────────

export function ColorPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useI18n();

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-muted-foreground">{t('team.roleField.color')}</label>
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1.5 p-2 rounded-lg border border-border bg-background">
          {PRESET_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => onChange(color)}
              aria-label={color}
              className={`w-6 h-6 rounded-full transition-all hover:scale-110 ${value === color ? 'ring-2 ring-offset-2 ring-primary ring-offset-background scale-110' : 'hover:ring-1 hover:ring-border'}`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              type="color"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              aria-label={t('team.roleField.color')}
              className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
            />
            <div className="w-9 h-9 rounded-lg border border-border flex items-center justify-center cursor-pointer hover:border-ring transition-colors" style={{ backgroundColor: value }}>
              <svg className="w-4 h-4" style={{ color: getContrastColor(value) }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
            </div>
          </div>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#6366f1"
            aria-label={t('team.roleField.color')}
            className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm font-mono text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>
    </div>
  );
}

function getContrastColor(hex: string): string {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 128 ? '#000000' : '#ffffff';
}

// ─── Permissions Editor ─────────────────────────────────────────

export function PermissionsEditor({ value, onChange, t }: {
  value: Set<string>;
  onChange: (v: Set<string>) => void;
  t: (key: string, params?: Record<string, string>) => string;
}) {
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  const togglePerm = (id: string) => {
    const next = new Set(value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const toggleGroup = (group: PermGroup) => {
    const allOn = group.permissions.every((p) => value.has(p.id));
    const next = new Set(value);
    group.permissions.forEach((p) => {
      if (allOn) next.delete(p.id);
      else next.add(p.id);
    });
    onChange(next);
  };

  const selectAll = () => onChange(new Set(ALL_PERM_IDS));
  const clearAll = () => onChange(new Set());

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-muted-foreground">{t('team.permissions.title')}</label>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {value.size}/{ALL_PERM_IDS.length}
          </span>
          <button type="button" onClick={selectAll} className="text-xs text-primary hover:underline">
            {t('team.permissions.selectAll')}
          </button>
          <button type="button" onClick={clearAll} className="text-xs text-muted-foreground hover:text-foreground hover:underline">
            {t('team.permissions.clearAll')}
          </button>
        </div>
      </div>
      <div className="rounded-lg border border-border divide-y divide-border">
        {PERMISSION_GROUPS.map((group) => {
          const groupEnabled = group.permissions.filter((p) => value.has(p.id)).length;
          const allOn = groupEnabled === group.permissions.length;
          const isExpanded = expandedGroup === group.key;

          return (
            <div key={group.key}>
              <div
                role="button"
                tabIndex={0}
                className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setExpandedGroup(isExpanded ? null : group.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedGroup(isExpanded ? null : group.key);
                  }
                }}
              >
                <svg
                  className={`h-3.5 w-3.5 text-muted-foreground transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-sm font-medium text-foreground flex-1">
                  {t(`team.permissions.group.${group.key}`)}
                </span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${groupEnabled > 0 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                  {groupEnabled}/{group.permissions.length}
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleGroup(group); }}
                  className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                    allOn
                      ? 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/20'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {allOn ? t('team.permissions.disableAll') : t('team.permissions.enableAll')}
                </button>
              </div>
              {isExpanded && (
                <div className="px-3 pb-3 pt-1 space-y-1 bg-muted/20">
                  {group.permissions.map((perm) => {
                    const isOn = value.has(perm.id);
                    return (
                      <label
                        key={perm.id}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                          isOn ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-muted/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isOn}
                          onChange={() => togglePerm(perm.id)}
                          className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-foreground">{t(`team.permissions.perm.${perm.id}`)}</span>
                          <p className="text-xs text-muted-foreground">{t(`team.permissions.permDesc.${perm.id}`)}</p>
                        </div>
                        <span className="text-[10px] rounded-full bg-muted px-1.5 py-0.5 text-muted-foreground">{perm.scope}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Permissions Badges (compact view for cards) ────────────────

export function PermissionsBadges({ permissions, t }: {
  permissions: { id: string }[];
  t: (key: string) => string;
}) {
  if (!permissions || permissions.length === 0) {
    return <span className="text-xs text-muted-foreground italic">{t('team.permissions.none')}</span>;
  }

  const grouped: Record<string, number> = {};
  for (const p of permissions) {
    for (const g of PERMISSION_GROUPS) {
      if (g.permissions.some((gp) => gp.id === p.id)) {
        grouped[g.key] = (grouped[g.key] || 0) + 1;
        break;
      }
    }
  }

  return (
    <div className="flex flex-wrap gap-1">
      {Object.entries(grouped).map(([key, count]) => (
        <span
          key={key}
          className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium"
        >
          {t(`team.permissions.group.${key}`)} ({count})
        </span>
      ))}
    </div>
  );
}
